if (!window.__setsProductLoaded) {
  window.__setsProductLoaded = true;

  // ── Fetch interceptor ──────────────────────────────────────────────────────
  // • Dispatches cart:updated after any cart-mutating request so all UI
  //   components (count bubble, drawer, set buttons) stay in sync.
  // • Intercepts /cart/change.js removals (quantity → 0) for set-group items:
  //   shows the confirmation popup BEFORE anything is removed so both items
  //   can be removed together when the customer confirms.
  (function () {
    const originalFetch = window.fetch;
    window.fetch = async function (...args) {
      const url =
        typeof args[0] === 'string'
          ? args[0]
          : (args[0] && args[0].url) || '';
      const opts   = args[1] || {};
      const method = (opts.method || 'GET').toUpperCase();

      // ── Set-group removal guard ──────────────────────────────────────────
      // Only fires for POST /cart/change.js with quantity:0 and when the
      // sets-product instance is ready.
      if (
        method === 'POST' &&
        url.includes('/cart/change') &&
        window.__setsProductInstance
      ) {
        let body = {};
        try { body = JSON.parse(opts.body || '{}'); } catch (e) { /* ignore */ }

        if (body.quantity === 0) {
          const cart = await originalFetch('/cart.js').then(r => r.json());

          // The cart drawer sends body.line (1-based line index).
          // Direct cart API calls may send body.id (key or variant_id).
          let item;
          if (body.line !== undefined) {
            item = cart.items[parseInt(body.line, 10) - 1];
          } else if (body.id !== undefined) {
            item = cart.items.find(
              i => i.key === body.id || String(i.variant_id) === String(body.id)
            );
          }

          // Gate: only intercept when the global "Enable Combined Add to Cart"
          // setting is on. The value is stamped onto the popup element by
          // sets-remove-confirm.liquid (rendered in theme.liquid) so it is
          // readable on every page — cart page, PDP, anywhere.
          const confirmOverlay = document.getElementById('sets-remove-confirm-overlay');
          const combinedModeActive = confirmOverlay && confirmOverlay.dataset.combined === 'true';

          if (item && item.properties && item.properties._set_group_id && combinedModeActive) {
            const setGroupId = item.properties._set_group_id;
            const setItems   = cart.items.filter(
              i => i.properties && i.properties._set_group_id === setGroupId
            );
            const instance = window.__setsProductInstance;

            // Ask for confirmation before removing anything.
            const confirmed = await instance._showRemoveConfirm();
            if (!confirmed) {
              // User cancelled — fetch the current cart WITH sections via a no-op
              // update so Dawn's updateQuantity can re-render the drawer/page
              // back to its original state without throwing a JS error.
              const noOpResp = await originalFetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  updates: {},
                  sections: body.sections || 'cart-drawer',
                  sections_url: body.sections_url || window.location.pathname
                })
              });
              const noOpBody = await noOpResp.text();
              return new Response(noOpBody, {
                status: 200,
                headers: { 'Content-Type': 'application/json' }
              });
            }

            // User confirmed — remove every item in the set group at once.
            const updates = {};
            setItems.forEach(i => { updates[i.key] = 0; });

            // Pass the caller's sections list through so the response
            // contains whatever HTML the caller (cart page or cart drawer)
            // needs to re-render its own UI.
            const updateResp = await originalFetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                updates,
                sections: body.sections || 'cart-drawer',
                sections_url: body.sections_url || window.location.pathname
              })
            });
            const updatedCart = await updateResp.json();

            instance._hideRemoveConfirm();
            instance.updateCartUI(updatedCart);
            instance._setCombinedBtnRemoved(new Set(setItems.map(i => i.variant_id)));
            document.dispatchEvent(new CustomEvent('cart:updated'));

            // Return a /cart/change.js-shaped response so the caller doesn't crash.
            return new Response(JSON.stringify(updatedCart), {
              status: 200,
              headers: { 'Content-Type': 'application/json' }
            });
          }
        }
      }

      // ── Default pass-through ─────────────────────────────────────────────
      const response = await originalFetch.apply(this, args);
      if (
        url.includes('/cart/add') ||
        url.includes('/cart/update') ||
        url.includes('/cart/change') ||
        url.includes('/cart/clear')
      ) {
        document.dispatchEvent(new CustomEvent('cart:updated'));
      }
      return response;
    };
  })();

  class SetsProduct {
    constructor() {
      this._syncing = false; // guard for syncOrphanedSetItems re-entrancy
      this.bindEvents();
      // Only run the initial cart fetch on pages that actually have set sections.
      // sets-product.js is loaded globally (theme.liquid) so these guards prevent
      // unnecessary /cart.js calls on the cart page, checkout, etc.
      if (document.querySelector('.set_section')) {
        this.checkCartStatus();
        this.updateCartCountOnLoad();
      }
    }

    // ── Mode detection ─────────────────────────────────────────────────────────

    isCombinedMode(container) {
      return container && container.dataset.combined === 'true';
    }

    // ── Event wiring ───────────────────────────────────────────────────────────

    bindEvents() {
      document.addEventListener('click', (e) => {
        // "Add Both Products to Cart" button (combined mode only)
        const combinedBtn = e.target.closest('.sets-combined-btn');
        if (combinedBtn) {
          const container = combinedBtn.closest('.set_section');
          this.handleCombinedAddToCart(container);
          return;
        }

        // Size / variant selection buttons
        const button = e.target.closest('.size-btn');
        if (!button) return;
        const card = button.closest('.sets-product-card');
        if (!card) return;
        const container = card.closest('.set_section') || document;

        if (this.isCombinedMode(container)) {
          // Combined mode: clicking a size only selects it — no individual cart add
          this.handleSizeSelectCombined(button, card, container);
        } else {
          // Default mode: clicking a size adds or removes it from cart immediately
          this.handleClick(button);
        }
      });

      document.addEventListener('cart:updated', () => {
        this.checkCartStatus();
        this.updateCartCountOnLoad();
        // Remove the partner item when one item from a linked set is removed
        this.syncOrphanedSetItems();
      });
    }

    // ── Default (non-combined) handlers — unchanged from original ─────────────

    handleClick(button) {
      const card = button.closest('.sets-product-card');
      if (!card) return;
      const container = card.closest('.set_section') || document;
      const cardsInContainer = Array.from(
        container.querySelectorAll('.sets-product-card')
      );
      const isLastCard =
        cardsInContainer.indexOf(card) === cardsInContainer.length - 1;
      const isQuickAdd = !!button.closest('quick-add-modal');
      const variantId = button.dataset.variantId;
      const productId = card.dataset.productId;

      if (button.classList.contains('in-cart')) {
        this.removeFromCart(variantId, productId);
      } else {
        this.addToCart(variantId, productId, isLastCard, isQuickAdd);
      }
    }

    async addToCart(variantId, productId, openDrawer, isQuickAdd) {
      this.showStatus(productId, 'Adding...', 'adding');
      try {
        const quickAddNotification = isQuickAdd
          ? document.querySelector('quick-add-notification')
          : null;
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            sections: quickAddNotification
              ? 'cart-drawer,cart-icon-bubble'
              : 'cart-drawer',
            sections_url: window.location.pathname
          })
        });
        const cart = await response.json();
        this.showStatus(productId, 'Added to cart', 'added');
        this.updateCartUI(cart);
        if (quickAddNotification) {
          quickAddNotification.renderContents(cart);
        } else if (openDrawer) {
          this.openCartDrawer();
        }
        document.dispatchEvent(new CustomEvent('cart:updated'));
      } catch (err) {
        this.showStatus(productId, 'Error', 'error');
        console.error(err);
      }
    }

    async removeFromCart(variantId, productId) {
      this.showStatus(productId, 'Removing...', 'adding');
      try {
        const cart = await fetch('/cart.js').then((r) => r.json());
        const item = cart.items.find((i) => i.variant_id == variantId);
        if (!item) return;
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: item.key,
            quantity: 0,
            sections: 'cart-drawer',
            sections_url: window.location.pathname
          })
        });
        const updatedCart = await response.json();
        this.showStatus(productId, 'Removed', 'removed');
        this.updateCartUI(updatedCart);
        document.dispatchEvent(new CustomEvent('cart:updated'));
      } catch (err) {
        this.showStatus(productId, 'Error', 'error');
        console.error(err);
      }
    }

    // ── Combined mode: size selection ──────────────────────────────────────────

    handleSizeSelectCombined(button, card, container) {
      // In-cart items are managed through the cart drawer; don't re-select them.
      if (button.classList.contains('in-cart')) return;

      const productId = card.dataset.productId;
      const isAlreadySelected = button.classList.contains('selected');

      // Deselect all buttons in this card before toggling the clicked one
      card.querySelectorAll('.size-btn').forEach((btn) => {
        btn.classList.remove('selected');
        delete btn.dataset.wasSelected;
      });

      if (!isAlreadySelected) {
        button.classList.add('selected');
        button.dataset.wasSelected = 'true';
      }

      // Show which size is currently chosen under this card
      const selectedBtn = card.querySelector('.size-btn.selected');
      const statusEl = document.querySelector(
        `.cart-status[data-product-id="${productId}"] .status-message`
      );
      if (statusEl) {
        if (selectedBtn) {
          statusEl.className = 'status-message size-selected';
          statusEl.textContent = `Size: ${selectedBtn.dataset.size}`;
        } else {
          statusEl.className = 'status-message';
          statusEl.textContent = '';
        }
      }

      // Clear any stale validation message as soon as the customer interacts
      const validationMsg = container.querySelector('.sets-validation-msg');
      if (validationMsg) {
        validationMsg.textContent = '';
        validationMsg.classList.remove('visible');
      }
    }

    // ── Combined mode: add both products ──────────────────────────────────────

    async handleCombinedAddToCart(container) {
      if (!container) return;

      const cards = Array.from(container.querySelectorAll('.sets-product-card'));
      const validationMsg = container.querySelector('.sets-validation-msg');
      const combinedBtn = container.querySelector('.sets-combined-btn');

      // Collect one selected variant per card
      const selectedItems = [];
      cards.forEach((card) => {
        const selectedBtn = card.querySelector('.size-btn.selected');
        if (selectedBtn) {
          selectedItems.push({
            variantId: selectedBtn.dataset.variantId,
            productId: card.dataset.productId,
            size: selectedBtn.dataset.size
          });
        }
      });

      // ── Validation ───────────────────────────────────────────────────────────
      // Case 2 & 3: one or both products have no size selected
      if (selectedItems.length < cards.length) {
        if (validationMsg) {
          validationMsg.textContent = '*select both products to add to cart';
          validationMsg.classList.add('visible');
        }
        return;
      }

      // All products selected — clear any previous validation message
      if (validationMsg) {
        validationMsg.textContent = '';
        validationMsg.classList.remove('visible');
      }

      // ── Linking ──────────────────────────────────────────────────────────────
      // A unique group ID stamped on every line item as a private property
      // (_set_group_id is prefixed with _ so Shopify hides it from customers).
      // syncOrphanedSetItems() uses this ID to pair-remove items.
      const setGroupId = 'SET_' + Date.now();

      const items = selectedItems.map((item) => ({
        id: parseInt(item.variantId),
        quantity: 1,
        properties: { _set_group_id: setGroupId }
      }));

      // ── Loading state ─────────────────────────────────────────────────────────
      if (combinedBtn) {
        combinedBtn.disabled = true;
        combinedBtn.textContent = 'Adding...';
      }

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items,
            sections: 'cart-drawer',
            sections_url: window.location.pathname
          })
        });
        const result = await response.json();

        // Shopify returns { status: 422, description: '...' } on item errors
        if (result.status) {
          if (validationMsg) {
            validationMsg.textContent =
              result.description || 'Could not add to cart. Please try again.';
            validationMsg.classList.add('visible');
          }
          if (combinedBtn) {
            combinedBtn.disabled = false;
            combinedBtn.textContent = 'ADD TO BAG';
          }
          return;
        }

        // ── Success ───────────────────────────────────────────────────────────
        // Mark each selected button as in-cart; status is shown on the combined
        // button only — no per-card messages in combined mode.
        selectedItems.forEach((item) => {
          const card = container.querySelector(
            `.sets-product-card[data-product-id="${item.productId}"]`
          );
          if (!card) return;
          const btn = card.querySelector(
            `.size-btn[data-variant-id="${item.variantId}"]`
          );
          if (btn) {
            this.setState(btn, 'in-cart');
            delete btn.dataset.wasSelected;
          }
          // Clear any pending "Size: X" selection label under the card
          this.showStatus(item.productId, '', '');
        });

        if (combinedBtn) {
          combinedBtn.disabled = false;
          combinedBtn.classList.add('sets-combined-btn--added');
          combinedBtn.textContent = 'Added to Cart';
        }

        this.updateCartUI(result);
        this.openCartDrawer();
        document.dispatchEvent(new CustomEvent('cart:updated'));
      } catch (err) {
        if (validationMsg) {
          validationMsg.textContent = 'Error adding to cart. Please try again.';
          validationMsg.classList.add('visible');
        }
        if (combinedBtn) {
          combinedBtn.disabled = false;
          combinedBtn.textContent = 'ADD TO BAG';
        }
        console.error(err);
      }
    }

    async syncOrphanedSetItems() {
      if (this._syncing) return;
      this._syncing = true;
      try {
        const cart = await fetch('/cart.js').then((r) => r.json());

        const setItems = cart.items.filter(
          (item) => item.properties && item.properties._set_group_id
        );
        if (!setItems.length) return;

        const groups = {};
        setItems.forEach((item) => {
          const gid = item.properties._set_group_id;
          if (!groups[gid]) groups[gid] = [];
          groups[gid].push(item);
        });

        const updates = {};
        const orphanedVariantIds = new Set();
        Object.values(groups).forEach((items) => {
          if (items.length === 1) {
            updates[items[0].key] = 0;
            orphanedVariantIds.add(items[0].variant_id);
          }
        });

        if (!Object.keys(updates).length) return;

        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            updates,
            sections: 'cart-drawer',
            sections_url: window.location.pathname
          })
        });
        const updatedCart = await response.json();
        this.updateCartUI(updatedCart);
        this._setCombinedBtnRemoved(orphanedVariantIds);
      } catch (err) {
        console.error('Set group sync error:', err);
      } finally {
        this._syncing = false;
      }
    }

    // Show the "Both Items will be removed" modal.
    // Returns a Promise that resolves to true (confirmed) or false (cancelled).
    _showRemoveConfirm() {
      return new Promise((resolve) => {
        const overlay = document.getElementById('sets-remove-confirm-overlay');
        if (!overlay) { resolve(true); return; }

        const confirmBtn = overlay.querySelector('.sets-confirm-ok');
        const cancelBtn  = overlay.querySelector('.sets-confirm-cancel');

        // Reset button state before showing
        confirmBtn.textContent = 'Confirm';
        confirmBtn.disabled = false;
        cancelBtn.disabled   = false;

        overlay.removeAttribute('hidden');

        const cleanup = () => {
          confirmBtn.removeEventListener('click', onConfirm);
          cancelBtn.removeEventListener('click', onCancel);
          overlay.removeEventListener('click', onBackdrop);
          document.removeEventListener('keydown', onKeydown);
        };

        const onConfirm = () => {
          confirmBtn.textContent = 'Removing...';
          confirmBtn.disabled = true;
          cancelBtn.disabled  = true;
          cleanup();
          resolve(true);
        };

        const onCancel = () => {
          overlay.setAttribute('hidden', '');
          cleanup();
          resolve(false);
        };

        // Click on the dark backdrop (not the modal card) → cancel
        const onBackdrop = (e) => { if (e.target === overlay) onCancel(); };

        // Escape key → cancel
        const onKeydown  = (e) => { if (e.key === 'Escape') onCancel(); };

        confirmBtn.addEventListener('click', onConfirm);
        cancelBtn.addEventListener('click', onCancel);
        overlay.addEventListener('click', onBackdrop);
        document.addEventListener('keydown', onKeydown);
      });
    }

    _hideRemoveConfirm() {
      const overlay = document.getElementById('sets-remove-confirm-overlay');
      if (overlay) overlay.setAttribute('hidden', '');
    }

    // Show "Both Items Removed" on the PDP combined button for 2.5 s, then reset.
    // updateCombinedButtonState() will not overwrite this while the
    // --removed class is present (see guard in that method).
    _setCombinedBtnRemoved(orphanedVariantIds) {
      document.querySelectorAll('.set_section[data-combined="true"]').forEach((container) => {
        const owns = Array.from(container.querySelectorAll('.size-btn')).some(
          (btn) => orphanedVariantIds.has(parseInt(btn.dataset.variantId))
        );
        if (!owns) return;
        const btn = container.querySelector('.sets-combined-btn');
        if (!btn) return;
        btn.disabled = false;
        btn.classList.remove('sets-combined-btn--added');
        btn.classList.add('sets-combined-btn--removed');
        btn.textContent = 'Both Items Removed';
        setTimeout(() => {
          btn.classList.remove('sets-combined-btn--removed');
          btn.textContent = 'ADD TO BAG';
        }, 2500);
      });
    }

    // ── Cart status sync ──────────────────────────────────────────────────────

    async checkCartStatus() {
      try {
        const cart = await fetch('/cart.js').then((r) => r.json());

        document.querySelectorAll('.sets-product-card').forEach((card) => {
          const productId = card.dataset.productId;
          const buttons = card.querySelectorAll('.size-btn');
          const container = card.closest('.set_section');
          const inCombinedMode = this.isCombinedMode(container);
          let totalQty = 0;

          buttons.forEach((button) => {
            const variantId = parseInt(button.dataset.variantId);
            const item = cart.items.find((i) => i.variant_id === variantId);
            if (item) {
              totalQty += item.quantity;
              this.setState(button, 'in-cart');
            } else if (
              !inCombinedMode ||
              !button.classList.contains('selected')
            ) {
              // In combined mode, preserve the customer's pending size selection
              // so it isn't wiped by an unrelated cart:updated event.
              this.setState(button, 'available');
            }
          });

          if (inCombinedMode) {
            // In combined mode the button is the only status indicator.
            // Only preserve the "Size: X" selection label; never show "Added to cart".
            if (totalQty > 0) {
              this.showStatus(productId, '', '');
            } else {
              const hasSelected = Array.from(buttons).some((b) =>
                b.classList.contains('selected')
              );
              if (!hasSelected) this.showStatus(productId, '', '');
            }
          } else {
            // Original status with shopping-bag icon
            if (totalQty > 0) {
              const text =
                totalQty > 1
                  ? `Added to cart (${totalQty} sizes)`
                  : `Added to cart`;
              this.showStatus(
                productId,
                `<span class="status-icon">
                  <svg width="13" height="14" viewBox="0 0 13 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                   <path d="M12.7078 4.29375C12.6151 4.2004 12.5048 4.12637 12.3833 4.07595C12.2618 4.02553 12.1315 3.99971 12 4H10V3.5C10 2.57174 9.63125 1.6815 8.97487 1.02513C8.3185 0.368749 7.42826 0 6.5 0C5.57174 0 4.6815 0.368749 4.02513 1.02513C3.36875 1.6815 3 2.57174 3 3.5V4H1C0.734784 4 0.48043 4.10536 0.292893 4.29289C0.105357 4.48043 0 4.73478 0 5V11.75C0 12.9688 1.03125 14 2.25 14H10.75C11.3395 14.0002 11.9057 13.7697 12.3275 13.3578C12.5398 13.1553 12.7088 12.9119 12.8244 12.6423C12.9399 12.3727 12.9997 12.0824 13 11.7891V5C13.0004 4.86881 12.9748 4.73884 12.9246 4.61761C12.8745 4.49638 12.8008 4.38631 12.7078 4.29375ZM8.89031 7.5625L6.09031 11.0625C6.04433 11.12 5.98623 11.1665 5.92017 11.199C5.85411 11.2314 5.7817 11.2488 5.70813 11.25H5.7C5.62778 11.25 5.55642 11.2344 5.49082 11.2042C5.42522 11.1739 5.36694 11.1299 5.32 11.075L4.12 9.6725C4.07732 9.6226 4.04489 9.56478 4.02455 9.50234C4.00422 9.43991 3.99638 9.37407 4.00149 9.30861C4.0066 9.24314 4.02455 9.17933 4.05432 9.1208C4.08409 9.06227 4.1251 9.01018 4.175 8.9675C4.2249 8.92482 4.28272 8.89239 4.34516 8.87205C4.4076 8.85172 4.47343 8.84388 4.53889 8.84899C4.60436 8.8541 4.66817 8.87205 4.7267 8.90182C4.78523 8.93159 4.83732 8.9726 4.88 9.0225L5.6875 9.96594L8.10969 6.9375C8.19257 6.8339 8.31321 6.76747 8.44507 6.75282C8.57693 6.73816 8.70921 6.77649 8.81281 6.85938C8.91641 6.94226 8.98285 7.0629 8.9975 7.19476C9.01215 7.32662 8.97382 7.4589 8.89094 7.5625H8.89031ZM9 4H4V3.5C4 2.83696 4.26339 2.20107 4.73223 1.73223C5.20107 1.26339 5.83696 1 6.5 1C7.16304 1 7.79893 1.26339 8.26777 1.73223C8.73661 2.20107 9 2.83696 9 3.5V4Z" fill="#1D3D72"/>
                  </svg>
                </span>
                <span class="status-text">${text}</span>`,
                'added',
                true
              );
            } else {
              this.showStatus(productId, '', '');
            }
          }
        });

        // Sync the combined button label for all combined sections on the page
        document
          .querySelectorAll('.set_section[data-combined="true"]')
          .forEach((container) => {
            this.updateCombinedButtonState(container, cart);
          });
      } catch (err) {
        console.error('Cart sync error:', err);
      }
    }

    // Reflect live cart state on the combined button (e.g. after page reload
    // or after the partner item is removed via the cart drawer).
    updateCombinedButtonState(container, cart) {
      const cards = Array.from(container.querySelectorAll('.sets-product-card'));
      const combinedBtn = container.querySelector('.sets-combined-btn');
      if (!combinedBtn) return;

      // Don't overwrite the "Both Items Removed" confirmation while it's visible.
      if (combinedBtn.classList.contains('sets-combined-btn--removed')) return;

      const allInCart = cards.every((card) => {
        const buttons = card.querySelectorAll('.size-btn');
        return Array.from(buttons).some((btn) => {
          const variantId = parseInt(btn.dataset.variantId);
          return cart.items.some((item) => item.variant_id === variantId);
        });
      });

      if (allInCart) {
        combinedBtn.classList.add('sets-combined-btn--added');
        combinedBtn.textContent = 'Added to Cart';
      } else {
        combinedBtn.classList.remove('sets-combined-btn--added');
        combinedBtn.textContent = 'ADD TO BAG';
      }
    }

    // ── Shared helpers ─────────────────────────────────────────────────────────

    setState(button, state) {
      // 'selected' is also removed here so a combined-mode selection is
      // automatically cleared when the variant transitions to in-cart.
      button.classList.remove('available', 'in-cart', 'adding', 'selected');
      button.classList.add(state);
    }

    showStatus(productId, text, type, persist = false) {
      const el = document.querySelector(
        `.cart-status[data-product-id="${productId}"] .status-message`
      );
      if (!el) return;
      el.className = 'status-message ' + type;
      el.innerHTML = text;
    }

    updateCartUI(cart) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        if (cart.item_count > 0) {
          drawer.classList.remove('is-empty');
        } else {
          drawer.classList.add('is-empty');
        }
      }
      let countEls = document.querySelectorAll('.cart-count-bubble');
      if (!countEls.length) {
        const cartIcons = document.querySelectorAll('.header__icon--cart');
        cartIcons.forEach((icon) => {
          const bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          icon.style.position = 'relative';
          icon.appendChild(bubble);
        });
        countEls = document.querySelectorAll('.cart-count-bubble');
      }
      countEls.forEach((el) => {
        el.textContent = cart.item_count || 0;
        el.style.display = cart.item_count > 0 ? 'flex' : 'none';
      });
      if (cart.sections && cart.sections['cart-drawer']) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(
          cart.sections['cart-drawer'],
          'text/html'
        );
        const newDrawer = doc.querySelector('cart-drawer');
        const currentDrawer = document.querySelector('cart-drawer');
        if (newDrawer && currentDrawer) {
          currentDrawer.innerHTML = newDrawer.innerHTML;
        }
      }
    }

    openCartDrawer() {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;
      drawer.classList.add('active');
      drawer.classList.remove('is-empty');
      document.body.classList.add('overflow-hidden');
      const overlay = drawer.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', () => {
          drawer.classList.remove('active');
          document.body.classList.remove('overflow-hidden');
        });
      }
    }

    async updateCartCountOnLoad() {
      try {
        const cart = await fetch('/cart.js').then((r) => r.json());
        this.updateCartUI(cart);
      } catch (err) {
        console.error(err);
      }
    }
  }

  const initSetsProduct = () => {
    if (window.__setsProductInstance) return;
    window.__setsProductInstance = new SetsProduct();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSetsProduct);
  } else {
    initSetsProduct();
  }
}
